import { useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';

import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useUpdateMemberPositionsInRoster } from 'hooks/incident/useRoster';
import { fetchGroupsForUser } from 'actions/profileActions';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import { useActualIncidentId, useGroupGuid } from '../IAP/hooks/commonHooks';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

interface IncidentEditMemberDialogProps {
  show: boolean;
  onClose: () => void;
  member: RosterEntry;
}

interface EditMemberPositionFormValues {
  positionId: string;
  positionName: string;
}

interface EditMemberSeatFormValues {
  memberId: string;
  memberName: string;
  positions: EditMemberPositionFormValues[];
}

export default function IncidentEditMemberDialog({
  show,
  onClose,
  member,
}: IncidentEditMemberDialogProps) {
  const dispatch = useDispatch();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EditMemberSeatFormValues>({
    mode: 'onChange',
    defaultValues: {
      memberId: member.incidentMemberId,
      memberName: member.userName || member.emailAddress || '',
      positions: member.incidentPositions.map((position) => ({
        positionId: position.positionId,
        positionName: position.positionName,
      })),
    },
  });

  const incidentId = useActualIncidentId();
  const groupId = useGroupGuid();
  const incidentPositionsQuery = useGetPositionsForIncident();
  const incidentPositions = incidentPositionsQuery.data || [];

  const reduxGroupsForUser = useSelector((state: any) => {
    return state.app.groupsForUser;
  });

  const reduxCurrentlySelectedGroup = useSelector((state: any) => {
    return state.app.currentlySelectedGroup;
  });

  const { mutate: updateMemberPositions, isPending: isLoading } =
    useUpdateMemberPositionsInRoster(incidentId, groupId);

  // Fetch groups for the user when member changes
  useEffect(() => {
    if (member?.userId) {
      dispatch(fetchGroupsForUser({ user_guid: member.userId }));
    }
  }, [member?.userId, dispatch]);

  // Derive groups from Redux state
  const groups = useMemo(() => {
    if (reduxGroupsForUser && reduxGroupsForUser.userGuid === member?.userId) {
      interface GroupForUser {
        group_guid: string;
      }

      interface GroupsForUserState {
        userGuid: string;
        groups: GroupForUser[];
      }

      const typedReduxGroupsForUser = reduxGroupsForUser as GroupsForUserState;

      return typedReduxGroupsForUser.groups.map(
        (group: GroupForUser) => group.group_guid
      );
    }
    return [];
  }, [reduxGroupsForUser, member?.userId]);

  const isInGroup =
    reduxCurrentlySelectedGroup?.group_guid &&
    groups.includes(reduxCurrentlySelectedGroup.group_guid);

  const onEditMemberSeatSubmit = (data: EditMemberSeatFormValues): void => {
    const positionIds = data.positions.map((position) => position.positionId);
    updateMemberPositions({
      incidentMemberId: member.incidentMemberId,
      newPositions: positionIds.map((positionId) => ({
        positionId,
        isLeader: false, // Current UI doesn't support editing leader status
      })),
    });
    onClose();
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        backdrop="static"
        keyboard={false}
      >
        <form onSubmit={handleSubmit(onEditMemberSeatSubmit)}>
          <Modal.Header closeButton closeVariant="white">
            <Modal.Title>Edit Member Seat</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Member:</label>
              <Controller
                control={control}
                name="memberName"
                rules={{ required: false }}
                render={({ field: { value } }) => (
                  <StylishNewInput
                    readOnly
                    disabled
                    type="text"
                    value={value}
                  />
                )}
              />
            </div>
            <div>
              <label className="form-label">Seat:</label>
              {isInGroup ? (
                <>
                  <Controller
                    control={control}
                    name="positions"
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <StylishNewSelect
                        options={incidentPositions.map(({ name, id }) => {
                          return { value: id, label: name };
                        })}
                        value={value.map((position) => ({
                          value: position.positionId,
                          label: position.positionName,
                        }))}
                        onChange={(e) => {
                          onChange(
                            e.map((pos: { value: string; label: string }) => ({
                              positionId: pos.value,
                              positionName: pos.label,
                            }))
                          );
                        }}
                        placeholder={'Assign a Role'}
                        isClearable={false}
                        isSearchable={true}
                        isMulti={true}
                        isDisabled={false}
                      />
                    )}
                  />
                  {errors.positions && errors.positions.length == 0 && (
                    <span className="form-text form-error">
                      This field is required
                    </span>
                  )}
                </>
              ) : (
                <>
                  <span className="form-text form-error multiline">
                    You must add the user to the incident group before assigning
                    seats.
                  </span>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={onClose}
                disabled={isLoading}
              >
                Close
              </StylishNewButton>
              {isInGroup ? (
                <StylishNewButton
                  type="submit"
                  className="button--primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Saving...' : 'Save Seat'}
                </StylishNewButton>
              ) : (
                <Link to="/groups" className="button button--primary">
                  Manage Groups
                </Link>
              )}
            </div>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
}
